import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Landmark, Scale, Search, Stamp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Pagination } from '@/components/common/Pagination';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { CollapsibleFilters, FilterField, FilterToggleButton } from '@/components/common/CollapsibleFilters';
import { TableHeaderControls } from '@/components/common/TableHeaderControls';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useDebounce } from '@/lib/hooks/useDebounce';
import type { LeoEntry, LeoFilters, LeoStatus, LeoType } from '../types';
import { useLeoColumns } from './LeoColumns';

const ALL = 'ALL';

const TYPE_ICONS: Record<LeoType, React.ComponentType<{ className?: string }>> = {
  COURT: Scale,
  GOVERNMENT_BODY: Landmark,
  NOTARY: Stamp,
};

interface LeoDataTableProps {
  data: LeoEntry[];
  total: number;
  filters: LeoFilters;
  isLoading?: boolean | undefined;
  isError?: boolean | undefined;
  onFiltersChange: <K extends keyof LeoFilters>(key: K, value: LeoFilters[K]) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onClearFilters?: (() => void) | undefined;
  onEdit?: ((entry: LeoEntry) => void) | undefined;
  onVerify?: ((entry: LeoEntry) => void) | undefined;
  onMerge?: ((entry: LeoEntry) => void) | undefined;
  onArchive?: ((entry: LeoEntry) => void) | undefined;
  onCreate?: (() => void) | undefined;
  hasAccess?: ((resource: string, action: string) => boolean) | undefined;
  isVerifying?: boolean | undefined;
  isArchiving?: boolean | undefined;
  /** Page-header slot for the Columns/Filters buttons (TableHeaderControls). */
  controlsContainer?: HTMLElement | null;
}

// Case 360° (Stage 8) — LEO directory data table (same shape as
// MattersDataTable / ClientsDataTable).
export function LeoDataTable({
  data,
  total,
  filters,
  isLoading,
  isError,
  onFiltersChange,
  onPageChange,
  onLimitChange,
  onClearFilters,
  onEdit,
  onVerify,
  onMerge,
  onArchive,
  onCreate,
  hasAccess,
  isVerifying,
  isArchiving,
  controlsContainer,
}: LeoDataTableProps) {
  const { t } = useTranslation();
  const [columnVisibility, setColumnVisibility] = useColumnVisibility('leo');
  const tableZoom = useTableZoom();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [rowSelection, setRowSelection] = React.useState({});

  const [searchValue, setSearchValue] = React.useState(filters.s ?? '');
  const debouncedSearch = useDebounce(searchValue, 300);
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const trimmed = debouncedSearch.trim();
    const newValue = trimmed.length >= 2 ? trimmed : undefined;
    if (newValue !== filters.s) {
      onFiltersChange('s', newValue);
    }
  }, [debouncedSearch]);

  const columns = useLeoColumns({
    onEdit,
    onVerify,
    onMerge,
    onArchive,
    hasAccess,
    isVerifying,
    isArchiving,
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: { columnVisibility, rowSelection },
  });

  const currentPage = filters.page ?? 0;
  const pageSize = filters.limit ?? 20;

  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filters.s) count++;
    if (filters.status) count++;
    return count;
  }, [filters]);

  const handleClearFilters = () => {
    setSearchValue('');
    onClearFilters?.();
  };

  return (
    <div className="w-full space-y-4">
      {/* LEO Type Toggle */}
      <ToggleGroup
        type="single"
        size="sm"
        className="justify-start gap-1"
        value={filters.type ?? 'none'}
        onValueChange={(item) =>
          onFiltersChange('type', item === 'none' ? undefined : (item as LeoType))
        }
      >
        <ToggleGroupItem
          value="none"
          aria-label="All"
          className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
        >
          {t('all')}
        </ToggleGroupItem>
        {(Object.keys(TYPE_ICONS) as LeoType[]).map((lt) => {
          const Icon = TYPE_ICONS[lt];
          return (
            <ToggleGroupItem
              key={lt}
              value={lt}
              aria-label={lt}
              className="rounded-full border px-4 data-[state=on]:bg-primary data-[state=on]:text-white"
            >
              <Icon className="inline mr-1.5 h-4 w-4" />
              {t(`leo.type.${lt}`)}
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>

      {/* Table Actions Bar */}
      <div className="flex items-center justify-between px-2">
        <div className="text-sm text-muted-foreground">
          {total > 0 && (
            <span>
              {t('showing')}{' '}
              {Math.min(currentPage * pageSize + 1, total)}-{Math.min((currentPage + 1) * pageSize, total)}{' '}
              {t('of')} {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <TableHeaderControls container={controlsContainer}>
            <ColumnVisibility table={table} />
            <FilterToggleButton
              activeFilterCount={activeFilterCount}
              open={filtersOpen}
              onToggle={() => setFiltersOpen((v) => !v)}
            />
          </TableHeaderControls>
        </div>
      </div>

      {/* Collapsible Filters */}
      <CollapsibleFilters
        open={filtersOpen}
        activeFilterCount={activeFilterCount}
        onClearFilters={handleClearFilters}
        gridCols={2}
      >
        {/* Search */}
        <FilterField label={t('search')}>
          <div className="relative">
            <Input
              placeholder={t('leo.searchPlaceholder')}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pr-10 h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
            />
            <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </FilterField>

        {/* Status */}
        <FilterField label={t('leo.statusLabel')}>
          <Select
            value={filters.status ?? ALL}
            onValueChange={(v) =>
              onFiltersChange('status', v === ALL ? undefined : (v as LeoStatus))
            }
          >
            <SelectTrigger className="w-full !h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('all')}</SelectItem>
              <SelectItem value="DRAFT">{t('leo.status.DRAFT')}</SelectItem>
              <SelectItem value="VERIFIED">{t('leo.status.VERIFIED')}</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>
      </CollapsibleFilters>

      {/* Table */}
      <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-[#f8fafc] hover:bg-[#f8fafc]">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[11px] font-semibold uppercase tracking-wider text-[#64748b]">
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-red-500">
                  {t('leo.loadError')}
                </TableCell>
              </TableRow>
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={
                    row.original?.deletedAt ? 'opacity-50 hover:bg-[#f8fafc]' : 'hover:bg-[#f8fafc]'
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="py-8">
                    <h3 className="mb-2 text-xl font-medium text-[#1a2b4a]">{t('leo.empty')}</h3>
                    <p className="text-[#64748b]">{t('leo.emptyDesc')}</p>
                    {hasAccess?.('leo', 'create') && onCreate && (
                      <Button className="mt-4" onClick={onCreate}>
                        {t('leo.add')}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        leadingControls={<TableZoomControls zoom={tableZoom} />}
        page={currentPage}
        limit={pageSize}
        total={total}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
        isLoading={isLoading}
      />
    </div>
  );
}
